import React from "react";
import PropTypes from "prop-types";
//Styles
import styled from "styled-components";
import { darken } from "polished";

const ContentContainer = styled.div`
  background-color: ${props => props.backgroundColor};
  box-shadow: ${props => props.boxShadow};

  border: ${props => props.border};
  border-radius: ${props => props.borderRadius};
  border-top: ${props => props.borderTop};
  border-bottom: ${props => props.borderBottom};
  border-left: ${props => props.borderLeft};

  margin: ${props => props.margin};
  margin-top: ${props => props.marginTop};
  margin-right: ${props => props.marginRight};
  margin-bottom: ${props => props.marginBottom};
  margin-left: ${props => props.marginLeft};

  padding: ${props => props.padding};
  padding-top: ${props => props.paddingTop};
  padding-bottom: ${props => props.paddingBottom};
  padding-right: ${props => props.paddingRight};
  padding-left: ${props => props.paddingLeft};

  color: ${props => props.color};
  text-align: ${props => props.textAlign};
  transform: ${props => props.transform};

  display: ${props => props.display};
  justify-content: ${props => props.justifyContent};
  flex-direction: ${props => props.flexDirection};
  align-items: ${props => props.alignItems};
  flex-grow: ${props => props.flexGrow};

  width: ${props => props.width};
  height: ${props => props.height};
  min-height: ${props => props.minHeight};
  max-height: ${props => props.maxHeight};
  z-index: ${props => props.zIndex};
  cursor: ${props => props.cursor};

  position: ${props => props.position};
  top: ${props => props.top};
  right: ${props => props.right};
  bottom: ${props => props.bottom};
  left: ${props => props.left};

  overflow: ${props => props.overflow};
  overflow-x: ${props => props.overflowX};

  float: ${props => props.float};
  clear: ${props => props.clear};

  ${props =>
    props.verticalAlign ? "display: flex; align-items: center;" : null};

  ${props =>
    props.hasLink &&
    `:hover {
    ${props.lighten && "opacity: 0.5;"};
    ${props.pointer && "cursor: pointer;"};
  }`}

  ${props =>
    props.behavior === "button" &&
    `&:hover {
    cursor: pointer;
    background-color: ${darken(0.1, props.backgroundColor)};
  }`}

  :last-child {
    border-bottom: ${props => props.lastChildBorderBottom};
  }

  ${props =>
    props.underlineWithAnimation &&
    `:after {
      content: "";
      display: block;
      position: absolute;
      width: ${props.active ? "100%" : "0"};
      height: 3px;
      background-color: ${
        props.active ? props.underlineAnimationColor || "#000" : "transparent"
      };
      transition: width 0.4s ease;
      -webkit-transition: width 0.4s ease;
      bottom: 0px;
    }
    :hover {
      :after {
        background-color: ${props.underlineAnimationColor || "#000"};
        width: 100%;
      }
    }`};

  // Large devices (desktops, less than 1200px)
  @media (max-width: 1199px) {
    margin-top: ${props => props.lgMargintop};
    margin-right: ${props => props.mobileLgMarginRight};
    margin-bottom: ${props => props.lgMarginBottom};
    margin-left: ${props => props.mobileLgMarginLeft};

    padding-top: ${props => props.mobileLgPaddingTop};
    padding-right: ${props => props.mobileLgPaddingRight};
    padding-bottom: ${props => props.mobileLgPaddingBottom};
    padding-left: ${props => props.mobileLgPaddingLeft};

    border-top: ${props => props.mobileLgBorderTop};
    border-bottom: ${props => props.mobileLgBorderBottom};

    display: ${props => props.mobileXlDisplay};
    width: ${props => props.mobileXlWidth};
  }

  // Medium devices (tablets, less than 992px)
  @media (max-width: 991px) {
    height: ${props => props.mdHeight};
    background-color: ${props => props.mobileMdBgColor};
    display: ${props => props.mobileMdDisplay};
    top: ${props => props.mobileMdTop};
    width: ${props => props.mobileMdWidth};

    margin-top: ${props => props.mdMargintop};
    margin-right: ${props => props.mobileMdMarginRight};
    margin-bottom: ${props => props.mdMarginBottom};
    margin-left: ${props => props.mobileMdMarginLeft};

    padding: ${props => props.mdPadding};
    padding-top: ${props => props.mobileMdPaddingTop};
    padding-bottom: ${props => props.mobileMdPaddingBottom};

    border-bottom: ${props => props.mobileMdBorderBottom};
    border-top: ${props => props.mobileMdBorderTop};
  }

  // Small devices (landscape phones, less than 768px)
  @media (max-width: 767px) {
    height: ${props => props.smHeight};
    width: ${props => props.mobileSmWidth};
    top: ${props => props.smTop};

    margin-top: ${props => props.smMargintop};
    margin-right: ${props => props.mobileSmMarginRight};
    margin-bottom: ${props => props.smMarginbottom};
    margin-left: ${props => props.mobileSmMarginLeft};

    padding-left: ${props => props.mobileSmPaddingLeft};
    padding-right: ${props => props.mobileSmPaddingRight};
    padding-top: ${props => props.mobileSmPaddingTop};
  }

  // Extra small devices (portrait phones, less than 576px)
  @media (max-width: 575px) {
    width: ${props => props.mobileXsWidth};
    height: ${props => props.mobileXsHeight};
    top: ${props => props.xsTop};
    display: ${props => props.mobileXsDisplay};

    margin-top: ${props => props.mobileXsMarginTop};
    margin-right: ${props => props.mobileXsMarginRight};
    margin-left: ${props => props.mobileXsMarginLeft};
    margin-bottom: ${props => props.mobileXsMarginBottom};

    padding-top: ${props => props.mobileXsPaddingTop};
    padding-bottom: ${props => props.mobileXsPaddingBottom};

    border-bottom: ${props => props.mobileXsBorderBottom};
    border-top: ${props => props.mobileXsBorderTop};
  }
`;

const types = {
  children: PropTypes.node,
  backgroundColor: PropTypes.string,
  boxShadow: PropTypes.string,

  border: PropTypes.string,
  borderRadius: PropTypes.string,
  borderTop: PropTypes.string,
  borderBottom: PropTypes.string,
  borderLeft: PropTypes.string,

  margin: PropTypes.string,
  marginTop: PropTypes.string,
  marginRight: PropTypes.string,
  marginBottom: PropTypes.string,
  marginLeft: PropTypes.string,

  padding: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingLeft: PropTypes.string,

  color: PropTypes.string,
  textAlign: PropTypes.string,
  transform: PropTypes.string,

  display: PropTypes.string,
  justifyContent: PropTypes.string,
  flexDirection: PropTypes.string,
  alignItems: PropTypes.string,
  flexGrow: PropTypes.string,

  width: PropTypes.string,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string,
  zIndex: PropTypes.string,
  cursor: PropTypes.string,

  position: PropTypes.string,
  top: PropTypes.string,
  right: PropTypes.string,
  bottom: PropTypes.string,
  left: PropTypes.string,

  overflow: PropTypes.string,
  overflowX: PropTypes.string,

  float: PropTypes.string,
  clear: PropTypes.string,

  underlineWithAnimation: PropTypes.bool,

  lgMargintop: PropTypes.string,
  mobileLgMarginRight: PropTypes.string,
  lgMarginBottom: PropTypes.string,
  mobileLgMarginLeft: PropTypes.string,

  mobileLgPaddingTop: PropTypes.string,
  mobileLgPaddingRight: PropTypes.string,
  mobileLgPaddingBottom: PropTypes.string,
  mobileLgPaddingLeft: PropTypes.string,

  mobileLgBorderTop: PropTypes.string,
  mobileLgBorderBottom: PropTypes.string,

  mobileXlDisplay: PropTypes.string,
  mobileXlWidth: PropTypes.string,

  mdHeight: PropTypes.string,
  mobileMdBgColor: PropTypes.string,
  mobileMdDisplay: PropTypes.string,
  mobileMdTop: PropTypes.string,
  mobileMdWidth: PropTypes.string,

  mdMargintop: PropTypes.string,
  mobileMdMarginRight: PropTypes.string,
  mdMarginBottom: PropTypes.string,
  mobileMdMarginLeft: PropTypes.string,

  mdPadding: PropTypes.string,
  mobileMdPaddingTop: PropTypes.string,
  mobileMdPaddingBottom: PropTypes.string,

  mobileMdBorderBottom: PropTypes.string,
  mobileMdBorderTop: PropTypes.string,

  smHeight: PropTypes.string,
  mobileSmWidth: PropTypes.string,
  smTop: PropTypes.string,

  smMargintop: PropTypes.string,
  mobileSmMarginRight: PropTypes.string,
  smMarginbottom: PropTypes.string,
  mobileSmMarginLeft: PropTypes.string,

  mobileSmPaddingLeft: PropTypes.string,
  mobileSmPaddingRight: PropTypes.string,
  mobileSmPaddingTop: PropTypes.string,

  mobileXsWidth: PropTypes.string,
  mobileXsHeight: PropTypes.string,
  xsTop: PropTypes.string,
  mobileXsDisplay: PropTypes.string,

  mobileXsMarginTop: PropTypes.string,
  mobileXsMarginRight: PropTypes.string,
  mobileXsMarginLeft: PropTypes.string,
  mobileXsMarginBottom: PropTypes.string,

  mobileXsPaddingTop: PropTypes.string,
  mobileXsPaddingBottom: PropTypes.string,

  mobileXsBorderBottom: PropTypes.string,
  mobileXsBorderTop: PropTypes.string
};

const defaults = {
  hasLink: true
};

ContentContainer.propTypes = types;

ContentContainer.defaultProps = defaults;

export default ContentContainer;

//Only used for the Storybook Props Table
export const WithProps = ({ children, ...props }) => (
  <ContentContainer {...props}>{children}</ContentContainer>
);

WithProps.propTypes = types;

WithProps.defaultProps = defaults;
